export type PackageId = "spin" | "hero" | "campaign" | "catalog";
export type PackageUnit = "SKU" | "product" | "10-SKU bundle";

export interface QuoteOptions {
  rush?: boolean;
}

const RUSH_MULTIPLIER = 1.25;

/** One sellable render package. Owns its own price rules. */
export class RenderPackage {
  constructor(
    readonly id: PackageId,
    readonly name: string,
    readonly unit: PackageUnit,
    readonly unitPriceUsd: number,
    readonly summary: string,
    readonly includes: readonly string[],
    readonly turnaround: string,
    /** Minimum quantity; for the bundle this is the flat-priced block size. */
    readonly minQty = 1,
    /** Price of each unit beyond `minQty` (bundle only). */
    readonly extraUnitUsd?: number,
  ) {}

  get isBundle(): boolean {
    return this.extraUnitUsd !== undefined;
  }

  total(qty: number, opts: QuoteOptions = {}): number {
    if (!Number.isInteger(qty) || qty < 1) throw new Error("Quantity must be a whole number of 1 or more.");
    if (qty < this.minQty) throw new Error(`${this.name} needs at least ${this.minQty} products.`);
    const base = this.isBundle
      ? this.unitPriceUsd + (qty - this.minQty) * (this.extraUnitUsd ?? 0)
      : this.unitPriceUsd * qty;
    return opts.rush ? Math.ceil(base * RUSH_MULTIPLIER) : base;
  }

  /** "$39 per SKU" style label for display. */
  priceLabel(): string {
    return `$${this.unitPriceUsd} ${this.unit === "10-SKU bundle" ? "for 10 SKUs" : `per ${this.unit}`}`;
  }
}

/** Launch price list. Prices are launch prices and may change. */
export class PackageCatalog {
  constructor(private readonly packages: readonly RenderPackage[]) {}

  static launch(): PackageCatalog {
    return new PackageCatalog([
      new RenderPackage(
        "spin",
        "360 spin",
        "SKU",
        39,
        "A smooth rotating loop of your product for listings and social.",
        ["5 second 360 loop, MP4 and GIF", "Square and vertical crops", "2 revision rounds"],
        "2 to 3 business days",
      ),
      new RenderPackage(
        "hero",
        "Hero renders",
        "SKU",
        99,
        "A spin plus polished stills on white and on a styled background.",
        [
          "360 spin loop",
          "3 hero stills, 4K, white and styled backgrounds",
          "Detail close-up",
          "2 revision rounds",
        ],
        "3 to 5 business days",
      ),
      new RenderPackage(
        "campaign",
        "Ad campaign set",
        "product",
        249,
        "Everything you need to run and test ads for one hero product.",
        [
          "360 spin loop",
          "5 ad stills in feed, story and square sizes",
          "One 9:16 short video clip for TikTok and Reels",
          "2 background or copy variants for A/B tests",
          "3 revision rounds",
        ],
        "5 to 7 business days",
      ),
      new RenderPackage(
        "catalog",
        "Catalog spin bundle",
        "10-SKU bundle",
        349,
        "Ten 360 spins at a bundle price. Extra SKUs are $29 each.",
        ["10 spin loops, consistent lighting and framing", "Square and vertical crops", "2 revision rounds per batch"],
        "7 to 10 business days",
        10,
        29,
      ),
    ]);
  }

  all(): readonly RenderPackage[] {
    return this.packages;
  }

  get(id: PackageId): RenderPackage {
    const found = this.packages.find((p) => p.id === id);
    if (!found) throw new Error(`Unknown package: ${String(id)}`);
    return found;
  }

  has(id: string): id is PackageId {
    return this.packages.some((p) => p.id === id);
  }

  /** Spin catalog work: the bundle from 10 SKUs, otherwise the single-SKU spin. */
  recommendForSkus(count: number): RenderPackage {
    return count >= 10 ? this.get("catalog") : this.get("spin");
  }
}
